import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { get, post } from '../utils/api';
import { Button } from './shadcn/button';
import { ReferralMiniPanel } from './ReferralWidgets';
import { CLASS_CAPACITY } from '../utils/constants/constants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './shadcn/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './shadcn/alert-dialog';
import { useToast } from '@/components/hooks/use-toast';
import { hasContentAccess } from '../utils/contentAccess';

const WEEKDAY_INDEX = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
};

// "Monday 21 September" from a YYYY-MM-DD UK date. Built at UTC noon so the calendar day never
// shifts with the viewer's timezone.
const formatLongDate = (dateUK) => {
  const [y, m, d] = (dateUK || '').split('-').map(Number);
  if (!y || !m || !d) return dateUK || '';
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
};

// "5:00pm" from "17:00". Falls back to the raw label if it isn't HH:mm.
const formatTime12 = (hhmm) => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm || '';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
};

function UpcomingLessons({ createCall, startHairCheck }) {
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [changeSlotModalOpen, setChangeSlotModalOpen] = useState(false);
  const [changeSlotContext, setChangeSlotContext] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // Next concrete class per child (assessment while pending) — drives the one-time welcome modal.
  const [nextSessions, setNextSessions] = useState([]);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  // The "seen" stamp is posted once per page load no matter how the modal is closed.
  const welcomeMarkedRef = useRef(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchUpcomingLessons();
    fetchNextSessions();
  }, [user?._id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!changeSlotModalOpen || !changeSlotContext) {
      setAvailableSlots([]);
      setSlotsError(null);
      setSelectedScheduleId(null);
      return;
    }
    const childId = changeSlotContext.childId?.toString?.() ?? changeSlotContext.childId;
    setLoadingSlots(true);
    setSlotsError(null);
    get(`/lesson-schedules/available-for-swap/${childId}`, true)
      .then((slots) => {
        setAvailableSlots(slots ?? []);
      })
      .catch((err) => {
        setSlotsError(err.response?.data?.message || 'Failed to load available slots');
        setAvailableSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [changeSlotModalOpen, changeSlotContext]);

  const fetchUpcomingLessons = async () => {
    try {
      setLoading(true);
      const lessons = await get(`/lesson-schedules/upcoming/${user._id}`, true);
      //console.log('Upcoming Lessons:', lessons);
      setUpcomingLessons(lessons);
      setError(null);
    } catch (error) {
      console.error('Error fetching upcoming lessons:', error);
      setError('Failed to load upcoming lessons');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextSessions = async () => {
    try {
      const data = await get('/lesson-schedules/next-sessions', true);
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      setNextSessions(sessions);
      // One-time welcome: only for a parent with an assessment still to come who hasn't dismissed it.
      if (!data?.welcomeModalSeen && sessions.some((s) => s.role === 'assessment')) {
        setWelcomeOpen(true);
      }
    } catch (err) {
      // Non-fatal: the lesson cards still render without the modal.
      console.error('Error fetching next sessions:', err);
    }
  };

  // Any close (Got it, Escape, tapping outside) counts as seen — it must never show twice.
  const dismissWelcome = () => {
    setWelcomeOpen(false);
    if (welcomeMarkedRef.current) return;
    welcomeMarkedRef.current = true;
    post('/users/me/welcome-modal-seen', {}, true).catch((err) => {
      console.error('Failed to record welcome modal dismissal:', err);
    });
  };

  const canJoinLesson = (day, time) => {
    const londonDay = currentTime.toLocaleDateString('en-US', {
      timeZone: 'Europe/London',
      weekday: 'long'
    });
    const londonTimeString = currentTime.toLocaleTimeString('en-GB', {
      timeZone: 'Europe/London',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    if (day !== londonDay) return false;

    const [lessonHour, lessonMinute] = time.split(':').map(Number);
    const [nowHour, nowMinute] = londonTimeString.split(':').map(Number);

    const lessonMinutes = lessonHour * 60 + lessonMinute;
    const nowMinutes = nowHour * 60 + nowMinute;

    return nowMinutes >= lessonMinutes - 1 && nowMinutes <= lessonMinutes + 35;
  };

  const handleJoinLesson = (childId, lessonScheduleId) => {
    createCall(childId, lessonScheduleId).then((url) => {
      startHairCheck(url);
    });
  };

  const openChangeSlotModal = (lesson) => {
    setChangeSlotContext({
      childId: lesson.childId,
      childName: lesson.childName,
      scheduleId: lesson.scheduleId,
    });
    setChangeSlotModalOpen(true);
  };

  const closeChangeSlotModal = () => {
    setChangeSlotModalOpen(false);
    setChangeSlotContext(null);
    setSelectedScheduleId(null);
    setSlotsError(null);
    setConfirmDialogOpen(false);
  };

  const handleConfirmSwapClick = () => {
    if (!selectedScheduleId) return;
    setConfirmDialogOpen(true);
  };

  const handleConfirmSlotChange = async () => {
    if (!changeSlotContext || !selectedScheduleId) return;
    const childId = changeSlotContext.childId?.toString?.() ?? changeSlotContext.childId;
    setSubmitting(true);
    setConfirmDialogOpen(false);
    try {
      await post(
        `/children/${childId}/slot-override`,
        {
          lessonScheduleId: changeSlotContext.scheduleId,
          overrideTargetScheduleId: selectedScheduleId,
        },
        true
      );
      toast({
        title: 'Slot changed',
        description: 'Your child\'s lesson slot has been updated for this week.',
      });
      closeChangeSlotModal();
      fetchUpcomingLessons();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to change slot. Please try again.';
      setSlotsError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const isSlotInPastWeekday = (slotDay) => {
    const currentWeekday = currentTime
      .toLocaleDateString('en-GB', {
        weekday: 'long',
        timeZone: 'Europe/London',
      })
      .toLowerCase();
    const currentDayIndex = WEEKDAY_INDEX[currentWeekday] ?? 99;
    const slotDayIndex = WEEKDAY_INDEX[(slotDay || '').toLowerCase()] ?? 99;
    return slotDayIndex < currentDayIndex;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  // Check if user has access
  if (user && !hasContentAccess(user)) {
    // Special message for deleted subscriptions
    if (user.accessStatus === 'subscription_deleted') {
      return (
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-4 text-red-800">Subscription Reactivation Required</h2>
            <p className="text-red-700 text-lg mb-4">
              Your subscription has been cancelled. To regain access to the classroom and lessons, please contact the Nova Learning team who will help reactivate your subscription.
            </p>
            <p className="text-red-600 text-sm">
              You can reach us at info@novalearning.co.uk or through our customer support channels.
            </p>
          </div>
        </div>
      );
    }

    // Default message for other non-access statuses
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <h2 className="text-2xl font-bold mb-4 text-amber-800">Subscription Required</h2>
          <p className="text-amber-700 text-lg">
            Please pay for your monthly subscription in order to join the classroom.
          </p>
        </div>
      </div>
    );
  }

  const assessmentSessions = nextSessions.filter((s) => s.role === 'assessment');

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      {/* Slim Refer & Earn strip (bar + invite link) — must never crowd out the lesson slots. */}
      <ReferralMiniPanel />

      <h2 className="text-2xl font-bold mb-6">Upcoming Lessons</h2>
      
      {upcomingLessons.map((lesson, index) => (
        <div 
          key={`${lesson.childId}-${lesson.scheduleId ?? lesson.day}-${lesson.time}`}
          className="border rounded-lg p-4 shadow-sm bg-white"
        >
          <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
            <div>
              <h3 className="text-lg font-semibold">{lesson.childName}</h3>
              <p className="text-gray-600">
                {lesson.day} at {lesson.time}
                {lesson.hasActiveOverride && (
                  <span className="ml-2 text-sm text-amber-600">(swapped this week)</span>
                )}
              </p>
            </div>
            <div className="text-right flex flex-col items-end gap-2 max-md:text-left max-md:items-stretch">
              {lesson.scheduleId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openChangeSlotModal(lesson)}
                  disabled={lesson.hasActiveOverride}
                  className="text-gray-700 border-gray-300 max-md:h-11"
                  title={lesson.hasActiveOverride ? "You've already changed your slot this week for this lesson." : undefined}
                >
                  Change slot
                </Button>
              )}
              {canJoinLesson(lesson.day, lesson.time) ? (
                <Button 
                  onClick={() => handleJoinLesson(lesson.childId, lesson.scheduleId)}
                  className="bg-indigo-600 hover:bg-indigo-700 max-md:h-12"
                >
                  Join Lesson
                </Button>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-2">
                    You'll be able to join the lesson 1 minute before it begins
                  </p>
                  <Button 
                    disabled
                    className="bg-gray-500 max-md:h-12"
                  >
                    Join Lesson
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      <Dialog
        open={changeSlotModalOpen}
        onOpenChange={(open) => {
          setChangeSlotModalOpen(open);
          if (!open) {
            setChangeSlotContext(null);
            setSelectedScheduleId(null);
            setSlotsError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change slot</DialogTitle>
            <DialogDescription>
              {changeSlotContext ? (
                <>Choose a different slot for {changeSlotContext.childName}. This swap applies until the end of the current week.</>
              ) : (
                <>Choose a different slot. This swap applies until the end of the current week.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {loadingSlots && (
              <div className="py-6 text-center text-gray-500 text-sm">Loading available slots…</div>
            )}
            {!loadingSlots && slotsError && (
              <div className="py-2 text-sm text-red-600">{slotsError}</div>
            )}
            {!loadingSlots && !slotsError && availableSlots.length === 0 && (
              <div className="py-4 text-center text-gray-500 text-sm">No other slots available for swap right now.</div>
            )}
            {!loadingSlots && availableSlots.length > 0 && (
              <>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-2">
                  {availableSlots.map((slot) => {
                    const isPastWeekday = isSlotInPastWeekday(slot.day);
                    return (
                      <button
                        key={slot.scheduleId}
                        type="button"
                        disabled={isPastWeekday}
                        onClick={() => {
                          if (isPastWeekday) return;
                          setSelectedScheduleId(slot.scheduleId);
                        }}
                        title={isPastWeekday ? 'This day has already passed this week.' : undefined}
                        className={`w-full text-left px-3 py-2 rounded text-sm border transition-colors ${
                          isPastWeekday
                            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                            : selectedScheduleId === slot.scheduleId
                              ? 'bg-indigo-100 border-indigo-300'
                              : 'hover:bg-gray-100 border-transparent'
                        }`}
                      >
                        <span className="font-medium">{slot.day}</span> at <span className="font-medium">{slot.time}</span>
                        {slot.effectiveHeadcount != null && (
                          <span className="text-gray-500 ml-1">Slots filled: ({slot.effectiveHeadcount}/{CLASS_CAPACITY})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={closeChangeSlotModal} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmSwapClick}
                    disabled={!selectedScheduleId || submitting}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    Confirm swap
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm slot change</AlertDialogTitle>
            <AlertDialogDescription className="font-bold text-green-800">
              You can only change your slot once per week. You won't be able to change again until next week. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSlotChange}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting ? 'Updating…' : 'Yes, change slot'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {upcomingLessons.length === 0 && (
        <p className="text-gray-500 text-center">No upcoming lessons scheduled</p>
      )}

      {/* One-time welcome after a trial signup: turn "I signed up for a maths thing" into a real
          appointment. Deliberately nothing else — no features, pricing or tour. */}
      <Dialog
        open={welcomeOpen}
        onOpenChange={(open) => {
          if (!open) dismissWelcome();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">You're all booked! 🎉</DialogTitle>
            <DialogDescription className="pt-1 text-base text-gray-800">
              {assessmentSessions.map((s, i) => (
                <React.Fragment key={`welcome-${s.childId}`}>
                  {i > 0 && <br />}
                  <span className="font-semibold">{s.childName}'s assessment</span> is on{' '}
                  <span className="font-semibold">
                    {formatLongDate(s.dateUK)} at {formatTime12(s.time)}
                  </span>
                </React.Fragment>
              ))}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Here's what happens next:</p>
            <ol className="space-y-3">
              {[
                ['Assessment', 'Your child completes a short 30-minute maths assessment.'],
                ['Trial lesson', "Straight afterwards, they'll join their first live Nova lesson with one of our tutors."],
                ['Your results', "We'll send you their assessment report on WhatsApp, showing their strengths and areas to work on."],
              ].map(([title, body], i) => (
                <li key={title} className="flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                    {i + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900">{title}</p>
                    <p>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p>
              When it's time, simply come back to this page and tap{' '}
              <span className="font-semibold">Join</span>.
            </p>
          </div>

          <DialogFooter>
            <Button onClick={dismissWelcome} className="w-full bg-indigo-600 hover:bg-indigo-700">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UpcomingLessons;